export type LogLevel = "debug" | "info" | "warn" | "error";

export type LogEntry = {
  ts: number;
  level: LogLevel;
  message: string;
  context?: unknown;
};

const MAX_BUFFER = 200;
const buffer: LogEntry[] = [];
const listeners = new Set<(entries: LogEntry[]) => void>();

function push(entry: LogEntry) {
  buffer.push(entry);
  if (buffer.length > MAX_BUFFER) buffer.shift();
  listeners.forEach((fn) => fn([...buffer]));
}

export function getLogBuffer(): LogEntry[] {
  return [...buffer];
}

export function subscribeLogs(fn: (entries: LogEntry[]) => void): () => void {
  listeners.add(fn);
  fn([...buffer]);
  return () => listeners.delete(fn);
}

function log(level: LogLevel, message: string, context?: unknown) {
  const entry: LogEntry = { ts: Date.now(), level, message, context };
  push(entry);
  const fn = console[level] ?? console.log;
  const lvl = level.toLocaleUpperCase("en-US");
  if (context !== undefined) fn(`[${lvl}] ${message}`, context);
  else fn(`[${lvl}] ${message}`);
}

export const logger = {
  debug: (message: string, context?: unknown) => log("debug", message, context),
  info: (message: string, context?: unknown) => log("info", message, context),
  warn: (message: string, context?: unknown) => log("warn", message, context),
  error: (message: string, context?: unknown) => log("error", message, context),
};

export function installGlobalErrorHandlers() {
  window.addEventListener("error", (ev) => {
    logger.error("window.error", {
      message: ev.message,
      filename: ev.filename,
      lineno: ev.lineno,
      colno: ev.colno,
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    logger.error("unhandledrejection", {
      reason: ev.reason instanceof Error ? ev.reason.message : String(ev.reason),
    });
  });
}
